/**
 * Menu — Dropdown or context menu for presenting a list of actions.
 *
 * Menu Component Guidelines
 * - Use Menu for contextual action lists triggered by a button or right-click.
 *   Prefer Menu over Popover when the content is a list of actions. Use Combobox
 *   when the user needs to search/filter options.
 * - Use `MenuCheckboxItem` for toggleable options within the menu (e.g. show/hide
 *   columns). Use `MenuRadioGroup` + `MenuRadioItem` for mutually exclusive choices
 *   (e.g. sort direction).
 * - Use `MenuSub` + `MenuSubTrigger` + `MenuSubContent` for nested submenus — keep
 *   nesting to one level deep to avoid complex navigation.
 * - Use `variant="destructive"` on `MenuItem` for dangerous actions (delete, remove)
 *   to give them red styling.
 * - Use `MenuLabel` for non-interactive section headings within a menu. Use
 *   `MenuSeparator` to visually divide groups.
 * @see https://base-ui.com/react/components/menu Base UI Menu documentation
 */
import type { ListItemContentProps } from "@replohq/lattice/components/list-item/ListItem";
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import React from "react";

import { ContextMenu as ContextMenuPrimitive } from "@base-ui/react/context-menu";
import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { ListItem } from "@replohq/lattice/components/list-item/ListItem";
import {
  PortalThemeScope,
  usePortalContainer,
} from "@replohq/lattice/components/portal-theme/PortalTheme";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { cn } from "@replohq/lattice/utils/cn";
import { CheckIcon, ChevronRightIcon } from "lucide-react";

function Menu({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function MenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
  const container = usePortalContainer();
  return (
    <MenuPrimitive.Portal
      data-slot="dropdown-menu-portal"
      container={container}
      {...props}
    />
  );
}

interface MenuTriggerProps<ClassName extends string = string>
  extends Omit<MenuPrimitive.Trigger.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const MenuTriggerInner = <ClassName extends string>(
  { className, UNSAFE_className, ...props }: MenuTriggerProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <MenuPrimitive.Trigger
      ref={ref}
      data-slot="dropdown-menu-trigger"
      className={cn(className, UNSAFE_className)}
      {...props}
    />
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const MenuTrigger = React.forwardRef(MenuTriggerInner) as <
  ClassName extends string,
>(
  props: MenuTriggerProps<ClassName> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

/**
 * ContextMenu — the right-click counterpart to {@link Menu}. It shares the same
 * popup parts (`MenuContent`, `MenuItem`, `MenuSeparator`, …); only the trigger
 * differs: instead of a button, {@link ContextMenuTrigger} marks a region that
 * opens the menu at the cursor on right-click (or long-press on touch). Use it
 * for row/card actions where a visible kebab would add clutter.
 */
function ContextMenu({ ...props }: ContextMenuPrimitive.Root.Props) {
  return <ContextMenuPrimitive.Root data-slot="context-menu" {...props} />;
}

interface ContextMenuTriggerProps<ClassName extends string = string>
  extends Omit<ContextMenuPrimitive.Trigger.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ContextMenuTriggerInner = <ClassName extends string>(
  { className, UNSAFE_className, ...props }: ContextMenuTriggerProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return (
    <ContextMenuPrimitive.Trigger
      ref={ref}
      data-slot="context-menu-trigger"
      className={cn(className, UNSAFE_className)}
      {...props}
    />
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const ContextMenuTrigger = React.forwardRef(ContextMenuTriggerInner) as <
  ClassName extends string,
>(
  props: ContextMenuTriggerProps<ClassName> &
    React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

interface MenuContentProps<ClassName extends string = string>
  extends Omit<MenuPrimitive.Popup.Props, "className">,
    Pick<
      MenuPrimitive.Positioner.Props,
      "align" | "alignOffset" | "side" | "sideOffset"
    > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  animated?: boolean;
}

function MenuContent<ClassName extends string>({
  align = "start",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  className,
  UNSAFE_className,
  animated = true,
  ...props
}: MenuContentProps<ClassName>) {
  const container = usePortalContainer();
  return (
    <MenuPrimitive.Portal container={container}>
      <PortalThemeScope>
        <MenuPrimitive.Positioner
          className="isolate z-[150] outline-none"
          align={align}
          alignOffset={alignOffset}
          side={side}
          sideOffset={sideOffset}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <MenuPrimitive.Popup
            data-slot="dropdown-menu-content"
            className={cn(
              animated &&
                "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 duration-100 data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2 origin-(--transform-origin)",
              "border border-border border-0.5 bg-sheet text-default min-w-32 rounded-xl p-2 shadow-md z-50 max-h-(--available-height) min-w-(--anchor-width) w-full overflow-x-hidden overflow-y-auto outline-none data-closed:overflow-hidden flex flex-col gap-0.5",
              className,
              UNSAFE_className,
            )}
            {...props}
          />
        </MenuPrimitive.Positioner>
      </PortalThemeScope>
    </MenuPrimitive.Portal>
  );
}

interface MenuGroupProps<ClassName extends string = string>
  extends Omit<MenuPrimitive.Group.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function MenuGroup({ className, UNSAFE_className, ...props }: MenuGroupProps) {
  return (
    <MenuPrimitive.Group
      data-slot="dropdown-menu-group"
      {...props}
      className={cn("flex flex-col gap-0.5", className, UNSAFE_className)}
    />
  );
}

interface MenuLabelProps<ClassName extends string = string>
  extends Omit<MenuPrimitive.GroupLabel.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  inset?: boolean;
}

function MenuLabel<ClassName extends string>({
  className,
  UNSAFE_className,
  inset,
  ...props
}: MenuLabelProps<ClassName>) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "text-default px-2 py-1 header-h6 data-inset:pl-8",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface MenuItemProps<ClassName extends string = string>
  extends Omit<MenuPrimitive.Item.Props, "className" | "children" | "label">,
    ListItemContentProps {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function MenuItem<ClassName extends string>({
  selected,
  className,
  UNSAFE_className,
  inset,
  variant,
  startEnhancer,
  endEnhancer,
  size,
  description,
  label,
  disabled,
  ...props
}: MenuItemProps<ClassName>) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      disabled={disabled}
      render={
        <ListItem
          selected={selected}
          className={className}
          UNSAFE_className={cn(UNSAFE_className, "group/dropdown-menu-item")}
          disabled={disabled}
          inset={inset}
          variant={variant}
          startEnhancer={startEnhancer}
          endEnhancer={endEnhancer}
          size={size}
          description={description}
          label={label}
        />
      }
      {...props}
    />
  );
}

function MenuSub({ ...props }: MenuPrimitive.SubmenuRoot.Props) {
  return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
}

interface MenuSubTriggerProps<ClassName extends string = string>
  extends Omit<
      MenuPrimitive.SubmenuTrigger.Props,
      "className" | "children" | "label"
    >,
    Omit<ListItemContentProps, "endEnhancer"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function MenuSubTrigger<ClassName extends string>({
  className,
  UNSAFE_className,
  inset,
  label,
  startEnhancer,
  size,
  variant,
  selected,
  description,
  ...props
}: MenuSubTriggerProps<ClassName>) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      {...props}
    >
      <ListItem
        className={className}
        UNSAFE_className={UNSAFE_className}
        inset={inset}
        startEnhancer={startEnhancer}
        size={size}
        variant={variant}
        selected={selected}
        description={description}
        endEnhancer={<ChevronRightIcon className="text-placeholder" />}
        label={label}
      />
    </MenuPrimitive.SubmenuTrigger>
  );
}

interface MenuSubContentProps<ClassName extends string = string>
  extends Omit<
    MenuContentProps<ClassName>,
    "align" | "alignOffset" | "side" | "sideOffset"
  > {
  align?: MenuContentProps["align"];
  alignOffset?: MenuContentProps["alignOffset"];
  side?: MenuContentProps["side"];
  sideOffset?: MenuContentProps["sideOffset"];
}

function MenuSubContent<ClassName extends string>({
  align = "start",
  alignOffset = -3,
  side = "right",
  sideOffset = 12,
  className,
  UNSAFE_className,
  ...props
}: MenuSubContentProps<ClassName>) {
  return (
    <MenuContent
      data-slot="dropdown-menu-sub-content"
      UNSAFE_className={cn(
        "min-w-[96px] w-auto p-1 shadow-lg",
        className,
        UNSAFE_className,
      )}
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      {...props}
    />
  );
}

interface MenuCheckboxItemProps<ClassName extends string = string>
  extends Omit<
      MenuPrimitive.CheckboxItem.Props,
      "className" | "children" | "label"
    >,
    Omit<ListItemContentProps, "endEnhancer" | "selected"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function MenuCheckboxItem<ClassName extends string>({
  className,
  UNSAFE_className,
  label,
  checked,
  inset,
  startEnhancer,
  size,
  variant,
  description,
  ...props
}: MenuCheckboxItemProps<ClassName>) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      checked={checked}
      render={
        <ListItem
          label={label}
          className={className}
          UNSAFE_className={cn(
            "data-checked:bg-menu-item-selected",
            UNSAFE_className,
          )}
          inset={inset}
          startEnhancer={startEnhancer}
          size={size}
          variant={variant}
          description={description}
          endEnhancer={
            <MenuPrimitive.CheckboxItemIndicator>
              <CheckIcon />
            </MenuPrimitive.CheckboxItemIndicator>
          }
        />
      }
      {...props}
    />
  );
}

interface MenuRadioGroupProps<ClassName extends string = string>
  extends Omit<MenuPrimitive.RadioGroup.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function MenuRadioGroup({
  className,
  UNSAFE_className,
  ...props
}: MenuRadioGroupProps) {
  return (
    <MenuPrimitive.RadioGroup
      className={cn("flex flex-col gap-0.5", className, UNSAFE_className)}
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  );
}

interface MenuRadioItemProps<ClassName extends string = string>
  extends Omit<
      MenuPrimitive.RadioItem.Props,
      "className" | "children" | "label"
    >,
    Omit<ListItemContentProps, "endEnhancer" | "selected"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function MenuRadioItem<ClassName extends string>({
  className,
  UNSAFE_className,
  label,
  inset,
  startEnhancer,
  size,
  variant,
  description,
  ...props
}: MenuRadioItemProps<ClassName>) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      render={
        <ListItem
          label={label}
          className={className}
          UNSAFE_className={cn(
            "data-checked:bg-menu-item-selected",
            UNSAFE_className,
          )}
          inset={inset}
          startEnhancer={startEnhancer}
          size={size}
          variant={variant}
          description={description}
          endEnhancer={
            <MenuPrimitive.RadioItemIndicator>
              <CheckIcon />
            </MenuPrimitive.RadioItemIndicator>
          }
        />
      }
      {...props}
    />
  );
}

function MenuSeparator({
  ...props
}: Omit<MenuPrimitive.Separator.Props, "className">) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      render={<Separator className="my-1 " />}
      {...props}
    />
  );
}

interface MenuShortcutProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"span">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function MenuShortcut<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: MenuShortcutProps<ClassName>) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "text-placeholder text-sm tracking-widest",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

export {
  Menu,
  ContextMenu,
  ContextMenuTrigger,
  MenuPortal,
  MenuTrigger,
  MenuContent,
  MenuGroup,
  MenuLabel,
  MenuItem,
  MenuCheckboxItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuShortcut,
  MenuSub,
  MenuSubTrigger,
  MenuSubContent,
};
